import { makeAutoObservable } from 'mobx';
import { t } from 'i18next';
import { AxiosInstance } from 'axios';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { IProfileStore } from './ProfileStore';
import { INotificationsStore } from './NotificationsStore';
import { SingleUserType } from 'types/GlobalTypes';

export interface IUsersStore {
  users: SingleUserType[];
  lastUpdated: number;
  isLoading: boolean;
  isRemovingUser: boolean;
  LoadData(apiInstance: AxiosInstance): Promise<void>;
  RemoveUser(apiInstance: AxiosInstance, userId: string, successCB, errorCB): Promise<void>;
}

export class UsersStore implements IUsersStore {
  lastUpdated: number = 0;
  users: SingleUserType[] = [];

  isLoading: boolean = false;
  isRemovingUser: boolean = false;

  _profileStore: IProfileStore;
  _notificationStore: INotificationsStore;

  constructor(profileStore: IProfileStore, notificationStore: INotificationsStore) {
    this._profileStore = profileStore;
    this._notificationStore = notificationStore;

    makeAutoObservable(this);
  }

  private setUsersData(data: SingleUserType[]) {
    this.users = data;
    this.lastUpdated = Date.now();
  }

  public async RemoveUser(apiInstance: AxiosInstance, userId: string, successCB, errorCB) {
    this.isRemovingUser = true;
    const response = await apiInstance.delete(`api/users/${userId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      successCB(response);
      this._notificationStore.displayNotification({
        response: response.data,
        variant: 'success',
        title: t('common.success'),
        message: t('toastMessages.userRemoved'),
      });

      this.isRemovingUser = false;
      return;
    }

    this.isRemovingUser = false;

    this._notificationStore.displayNotification({
      response: response.data,
      variant: 'error',
      title: t('common.error'),
      message: t('toastMessages.userRemoveFailed'),
    });
    errorCB(response);
  }

  public async LoadData(apiInstance: AxiosInstance) {
    this.isLoading = true;
    await this.fetchUsers(apiInstance);
    this.isLoading = false;
  }

  private async fetchUsers(apiInstance: AxiosInstance) {
    let response = await apiInstance.get(`api/users`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data?.data) {
      this.setUsersData(response.data.data);
    }

    return response;
  }
}